import Flex from 'components/ui/Flex';
import Table, { TableRow } from 'components/ui/Table';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { Fragment, MutableRefObject, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReturnAdditionalCostItem } from 'returnCenter-api';
import {
    returnSelectors,
    addAdditionalCostItem,
    removeAdditionalCostItem,
    updateAdditionalCostItem,
} from 'store/reducers/returnSlice';
import { configSelectors } from 'store/reducers/configSlice';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { styled } from 'styled-components';
import CostSelectCell, { CostOption } from './Cells/CostSelectCell';
import SkuCell from './Cells/SkuCell';
import QuantityCell from './Cells/QuantityCell';
import PriceCell from './Cells/PriceCell';
import CommentCell from './Cells/CommentCell';
import DescriptionCell from './Cells/DescriptionCell';
import ActionCell from './Cells/ActionCell';

type ReturnAdditionalCostsProps = {
    disabled?: boolean;
    cellInputRefs?: MutableRefObject<{
        [key: string]: unknown;
    }>;
    handleArrowKeyDown?: (e: React.KeyboardEvent, itemId: any, rowIndex: any, colIndex: any) => void;
    startRowIndex?: number;
};

const ReturnAdditionalCosts = ({
    disabled,
    cellInputRefs,
    handleArrowKeyDown,
    startRowIndex = 0,
}: ReturnAdditionalCostsProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [selectedCostOption, setSelectedCostOption] = useState<CostOption | null>(null);
    const [quantity, setQuantity] = useState<number>(1);
    const [price, setPrice] = useState<string>('');
    const [percentValue, setPercentValue] = useState<string>('');
    const [comment, setComment] = useState<string>('');

    const additionalCostItems = useAppSelector(returnSelectors.selectReturnAdditionalCostItems);
    const additionalCostOptions = useAppSelector(configSelectors.selectAdditionalCostOptions);
    const itemsCount = useAppSelector(returnSelectors.selectAdditionalCostItemsCount);
    const hasOptions = useAppSelector(configSelectors.selectHasAdditionalCostOptions);
    const settingsError = useAppSelector(configSelectors.selectSettingsError);
    const selectedOption = useAppSelector((state) =>
        configSelectors.selectSelectedAdditionalCostOption(state, selectedCostOption?.data ?? ''),
    );

    const hasPercentItem = additionalCostItems.some((item) => item.isPercent);

    const resetForm = () => {
        setSelectedCostOption(null);
        setQuantity(1);
        setPrice('');
        setPercentValue('');
        setComment('');
    };

    const handleAddCost = () => {
        if (!selectedOption || disabled) {
            return;
        }

        const isPercent = selectedOption.isPercent ?? false;

        // Validate: only one percent item allowed
        if (isPercent && hasPercentItem) {
            toast.warn(t('onlyOnePercentCostAllowed'));
            return;
        }

        const parsedQuantity = isPercent ? 1 : Number(quantity);
        const resolvedQuantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? Math.floor(parsedQuantity) : 1;

        // Resolve percent value (stored as 0-1 in DB)
        let resolvedPercent: number = 0;
        if (isPercent) {
            if (selectedOption.isManualPrice) {
                // Manual percent: user enters 0-100
                const parsed = Number(percentValue);
                if (percentValue.trim() === '' || Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
                    toast.warn(t('invalidPercent'));
                    return;
                }
                resolvedPercent = parsed / 100;
            } else {
                // Fixed percent from config (already stored as 0-1)
                resolvedPercent = selectedOption.percent ?? 0;
            }
        }

        const parsedPrice = !isPercent && selectedOption.isManualPrice
            ? price.trim() === ''
                ? undefined
                : Math.max(0, Number(price))
            : undefined;

        if (!isPercent && selectedOption.isManualPrice && parsedPrice !== undefined && Number.isNaN(parsedPrice)) {
            toast.warn(t('invalidPrice'));
            return;
        }

        const newItem: ReturnAdditionalCostItem = {
            sku: selectedOption.sku ?? undefined,
            name: selectedOption.description ?? selectedOption.sku ?? undefined,
            description: selectedOption.description ?? undefined,
            quantity: resolvedQuantity,
            comment: comment.trim() ? comment.trim() : undefined,
            isManualPrice: selectedOption.isManualPrice ?? false,
            isPercent,
            price: isPercent ? resolvedPercent : (parsedPrice !== undefined && !Number.isNaN(parsedPrice) ? parsedPrice : undefined),
        };

        dispatch(addAdditionalCostItem(newItem));
        resetForm();
    };

    const handleQuantityChange = (index: number, rawValue: string) => {
        const numeric = Number(rawValue);
        if (!Number.isFinite(numeric)) return;
        const resolved = numeric > 0 ? Math.floor(numeric) : 1;
        dispatch(updateAdditionalCostItem({ index, property: 'quantity', value: resolved || 1 }));
    };

    const handlePriceChange = (index: number, rawValue: string) => {
        const numeric = rawValue.trim() === '' ? undefined : Math.max(0, Number(rawValue));
        if (numeric !== undefined && Number.isNaN(numeric)) {
            return;
        }
        dispatch(updateAdditionalCostItem({ index, property: 'price', value: numeric }));
    };

    const handlePercentChange = (index: number, rawValue: string) => {
        const numeric = rawValue.trim() === '' ? undefined : Number(rawValue);
        if (numeric !== undefined && (Number.isNaN(numeric) || numeric < 0 || numeric > 100)) {
            return;
        }
        // Convert from UI (0-100) to DB (0-1)
        dispatch(updateAdditionalCostItem({ index, property: 'price', value: numeric !== undefined ? numeric / 100 : undefined }));
    };

    const handleCommentChange = (index: number, rawValue: string) => {
        dispatch(updateAdditionalCostItem({ index, property: 'comment', value: rawValue || undefined }));
    };

    const handleRemove = (index: number) => {
        if (disabled) return;
        dispatch(removeAdditionalCostItem(index));
    };

    const tableHeaders = [
        t('costType'),
        '',
        t('quantity'),
        t('price'),
        t('comment'),
        '', // Actions column
    ];

    const getRows = useCallback((): TableRow[] => {
        const rows: TableRow[] = [];

        // Add "add new" row if not disabled and options available
        if (!disabled && hasOptions) {
            const addRowIndex = startRowIndex;
            rows.push({
                key: 'add-new-cost',
                bgColor: 'fill',
                cells: [
                    {
                        el: (
                            <CostSelectCell
                                options={additionalCostOptions}
                                selectedKey={selectedCostOption?.data.id ?? ''}
                                onSelect={setSelectedCostOption}
                                disabled={disabled || !hasOptions}
                                rowIndex={addRowIndex}
                                colIndex={0}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                            />
                        ),
                    },
                    {
                        el: <SkuCell sku={selectedOption?.sku} />,
                    },
                    {
                        el: (
                            <QuantityCell
                                value={selectedOption?.isPercent ? 1 : quantity}
                                onChange={(val) => setQuantity(Math.max(1, Math.floor(Number(val) || 1)))}
                                disabled={disabled || selectedOption?.isPercent}
                                rowIndex={addRowIndex}
                                colIndex={2}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                            />
                        ),
                    },
                    {
                        el: selectedOption?.isPercent ? (
                            <PriceCell
                                value={selectedOption?.isManualPrice ? percentValue : (selectedOption?.percent != null ? selectedOption.percent * 100 : '')}
                                onChange={setPercentValue}
                                disabled={disabled}
                                isManualPrice={selectedOption?.isManualPrice}
                                isPercent
                                placeholder={selectedOption?.isManualPrice ? '0-100' : '-'}
                                rowIndex={addRowIndex}
                                colIndex={3}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                            />
                        ) : (
                            <PriceCell
                                value={price}
                                onChange={setPrice}
                                disabled={disabled}
                                isManualPrice={selectedOption?.isManualPrice}
                                placeholder={selectedOption?.isManualPrice ? '' : '-'}
                                rowIndex={addRowIndex}
                                colIndex={3}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                            />
                        ),
                    },
                    {
                        el: (
                            <CommentCell
                                value={comment}
                                onChange={setComment}
                                disabled={disabled}
                                rowIndex={addRowIndex}
                                colIndex={4}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                            />
                        ),
                    },
                    {
                        el: (
                            <ActionCell
                                mode="add"
                                onAdd={handleAddCost}
                                disabled={disabled || !selectedOption}
                                rowIndex={addRowIndex}
                                colIndex={5}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                                onAddComplete={() => {
                                    // Focus back to the select cell after adding with a small delay
                                    // to allow the form to reset and re-render
                                    setTimeout(() => {
                                        const selectRef = cellInputRefs?.current[`additionalCosts-${addRowIndex}-0`];
                                        if (selectRef && typeof (selectRef as any).focus === 'function') {
                                            (selectRef as any).focus();
                                        }
                                    }, 50);
                                }}
                            />
                        ),
                    },
                ],
            });
        }

        // Add existing items as rows
        additionalCostItems.forEach((item, index) => {
            const label = item.name || item.description || item.sku || t('missingInfo');
            const rowIndex = startRowIndex + (hasOptions ? 1 : 0) + index;
            rows.push({
                key: `cost-${item.sku ?? 'item'}-${index}`,
                cells: [
                    {
                        el: <DescriptionCell label={label} isManualPrice={item.isManualPrice} isPercent={item.isPercent} />,
                    },
                    {
                        el: <SkuCell sku={item.sku} />,
                    },
                    {
                        el: (
                            <QuantityCell
                                value={item.isPercent ? 1 : (item.quantity ?? 1)}
                                onChange={(val) => handleQuantityChange(index, val)}
                                disabled={disabled || item.isPercent}
                                rowIndex={rowIndex}
                                colIndex={2}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                            />
                        ),
                    },
                    {
                        el: (
                            <PriceCell
                                value={item.isPercent ? (item.price != null ? item.price * 100 : '') : item.price}
                                onChange={(val) => item.isPercent ? handlePercentChange(index, val) : handlePriceChange(index, val)}
                                disabled={disabled}
                                isManualPrice={item.isManualPrice}
                                isPercent={item.isPercent}
                                rowIndex={rowIndex}
                                colIndex={3}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                            />
                        ),
                    },
                    {
                        el: (
                            <CommentCell
                                value={item.comment ?? ''}
                                onChange={(val) => handleCommentChange(index, val)}
                                disabled={disabled}
                                rowIndex={rowIndex}
                                colIndex={4}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                            />
                        ),
                    },
                    {
                        el: (
                            <ActionCell
                                mode="remove"
                                onRemove={() => handleRemove(index)}
                                disabled={disabled}
                                rowIndex={rowIndex}
                                colIndex={5}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleArrowKeyDown}
                                onRemoveComplete={() => {
                                    // After removing, focus on previous cost row's delete button if exists, else select cell
                                    setTimeout(() => {
                                        // Calculate the first cost row index (after add row if it exists)
                                        const firstCostRowIndex = startRowIndex + (hasOptions ? 1 : 0);

                                        // Only try previous row if there are cost rows above this one
                                        if (rowIndex > firstCostRowIndex) {
                                            const prevRowRef =
                                                cellInputRefs?.current[`additionalCosts-${rowIndex - 1}-5`];
                                            if (prevRowRef && typeof (prevRowRef as any).focus === 'function') {
                                                (prevRowRef as any).focus();
                                                return;
                                            }
                                        }

                                        // Focus on the select cell (add row) if available
                                        if (hasOptions) {
                                            const selectRef =
                                                cellInputRefs?.current[`additionalCosts-${startRowIndex}-0`];
                                            if (selectRef && typeof (selectRef as any).focus === 'function') {
                                                (selectRef as any).focus();
                                            }
                                        }
                                    }, 50);
                                }}
                            />
                        ),
                    },
                ],
            });
        });

        return rows;
        // eslint-disable-next-line
    }, [
        additionalCostItems,
        additionalCostOptions,
        hasOptions,
        hasPercentItem,
        selectedCostOption?.data.id,
        selectedOption,
        quantity,
        price,
        percentValue,
        comment,
        disabled,
        t,
        cellInputRefs,
        handleArrowKeyDown,
        startRowIndex,
    ]);

    return (
        <Container column className="return-additional-costs">
            {settingsError && (
                <ErrorBanner align="center">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <Text fontSize={14} color="error">
                        {t('failedToLoadAdditionalCostOptions')}
                    </Text>
                </ErrorBanner>
            )}

            {getRows().length > 0 && (
                <Fragment>
                    <TitleWrapper className="mt-3" justify="between" align="center">
                        <Title as="h6" xBold fontSize={11} className="item-title">
                            {t('additionalCosts')}
                        </Title>
                    </TitleWrapper>
                    <Table
                        className="additional-costs-table f3-300 "
                        bgColor="fillWhite"
                        cols={tableHeaders.length}
                        headers={tableHeaders}
                        rows={getRows()}
                    />
                </Fragment>
            )}

            {itemsCount === 0 && !hasOptions && (
                <EmptyMessage>
                    <Text fontSize={14} italic color="textFaded">
                        {t('noAdditionalCostsAdded')}
                    </Text>
                </EmptyMessage>
            )}
        </Container>
    );
};

export default ReturnAdditionalCosts;

const Container = styled(Flex)``;

const TitleWrapper = styled(Flex)`
    border-bottom: 1px solid;
    margin-bottom: 0.5rem;
`;

const Title = styled(Text)`
    margin: 5px;
    margin-bottom: 0.5rem;
`;

const EmptyMessage = styled.div`
    padding: 1rem;
    text-align: center;
    background-color: ${({ theme }) => theme.colors.fillWhite};
`;

const ErrorBanner = styled(Flex)`
    padding: 0.75rem 1rem;
    margin-bottom: 0.5rem;
    background-color: #fdeaea;
    border: 1px solid ${({ theme }) => theme.colors.error};
    border-radius: var(--radius);
    gap: 0.5rem;
    color: ${({ theme }) => theme.colors.error};
`;
